import { DEFAULT_SETTINGS, STORAGE_KEY } from '@/core/constants'
import type { UserSettings } from '@/core/types'

/**
 * UserSettings 的最小 schema 校验：只保留已知字段，缺失字段用默认值补全。
 * 防止旧版本存档中无关键字段把 store 推到非法状态。
 */
export function sanitizeSettings(raw: unknown): UserSettings {
  if (!raw || typeof raw !== 'object') return { ...DEFAULT_SETTINGS }
  const r = raw as Partial<UserSettings>
  const validQuality = r.quality === 'low' || r.quality === 'medium' || r.quality === 'high'
  return {
    quality: validQuality ? r.quality! : DEFAULT_SETTINGS.quality,
    autoDegrade: typeof r.autoDegrade === 'boolean' ? r.autoDegrade : DEFAULT_SETTINGS.autoDegrade,
    masterVolume: clamp01(r.masterVolume, DEFAULT_SETTINGS.masterVolume),
    musicVolume: clamp01(r.musicVolume, DEFAULT_SETTINGS.musicVolume),
    sfxVolume: clamp01(r.sfxVolume, DEFAULT_SETTINGS.sfxVolume),
    invertY: typeof r.invertY === 'boolean' ? r.invertY : DEFAULT_SETTINGS.invertY,
    showStats: typeof r.showStats === 'boolean' ? r.showStats : DEFAULT_SETTINGS.showStats,
  }
}

function clamp01(v: unknown, fallback: number): number {
  if (typeof v !== 'number' || Number.isNaN(v)) return fallback
  return Math.min(1, Math.max(0, v))
}

/** 类 localStorage 的最小接口；测试时可注入 mock。 */
export interface StorageLike {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
  removeItem(key: string): void
}

/** 安全读取 settings，损坏 / 不存在时返回默认值。 */
export function loadSettings(storage: StorageLike | null = safeStorage()): UserSettings {
  if (!storage) return { ...DEFAULT_SETTINGS }
  try {
    const raw = storage.getItem(STORAGE_KEY)
    if (!raw) return { ...DEFAULT_SETTINGS }
    return sanitizeSettings(JSON.parse(raw))
  } catch {
    return { ...DEFAULT_SETTINGS }
  }
}

/** 安全写入 settings；失败时打印一次警告（如 storage 满 / 隐私模式 / 被禁用）。 */
let warnedSaveFailure = false
export function saveSettings(
  settings: UserSettings,
  storage: StorageLike | null = safeStorage(),
): void {
  if (!storage) return
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(settings))
  } catch (err) {
    // 持久化失败应让开发者/用户知道，但只警告一次以免刷屏。
    if (!warnedSaveFailure) {
      warnedSaveFailure = true
      console.warn('[wild-tides] saveSettings failed; settings will not persist.', err)
    }
  }
}

/** 测试辅助 :: 重置一次性警告 flag。 */
export function _resetPersistenceWarnings(): void {
  warnedSaveFailure = false
}

/** 在 SSR / 沙箱环境下安全获取 localStorage。 */
export function safeStorage(): StorageLike | null {
  try {
    if (typeof globalThis !== 'undefined' && 'localStorage' in globalThis) {
      return (globalThis as { localStorage: StorageLike }).localStorage
    }
  } catch {
    /* ignore */
  }
  return null
}
